import React from "react";
import moment from "moment";
import { RiVipCrownLine } from "react-icons/ri";
import { toast } from "sonner";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAtomValue } from "jotai";
import { AppStateAtom } from "@/store/AppStateAtom";
import { User } from "../types";

interface UserTableProps {
  users: User[];
  changeUserRole: (userId: string, role: string) => Promise<void>;
}

export const UsersTable = ({ users, changeUserRole }: UserTableProps) => {
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>User</TableHead>
            <TableHead>Role</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Joined</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users
            .sort((a, b) => {
              if (a.createdAt !== null && b.createdAt !== null) {
                return moment(b.createdAt).diff(moment(a.createdAt), "second");
              }
              return 0;
            })
            .map((user) => (
              <UserRow
                key={user.id}
                user={user}
                changeUserRole={changeUserRole}
              />
            ))}
        </TableBody>
      </Table>
    </div>
  );
};

interface UserRowProps {
  user: User;
  changeUserRole: (userId: string, role: string) => Promise<void>;
}

const UserRow = ({ user, changeUserRole }: UserRowProps) => {
  const appState = useAtomValue(AppStateAtom);

  const handleMakeAdmin = async () => {
    if (user.role === "admin") {
      toast.info("User is already an admin", {
        description: `${user.email} already has admin privileges`,
      });
      return;
    }

    await changeUserRole(user.id, "admin");
  };

  const handleMakeNormalUser = async () => {
    if (user.role === "normal") {
      toast.info("User is already a normal user", {
        description: `${user.email} already has normal user privileges`,
      });
      return;
    }

    await changeUserRole(user.id, "user");
  };

  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 rounded-lg bg-gray-600 flex items-center justify-center text-lg">
            {user.email[0].toUpperCase()}
          </div>
          <div className="space-y-0.5">
            <p className="font-medium">{user.email}</p>
            <p className="text-sm text-muted-foreground">
              ID: {user.id}{" "}
              {user.id === appState.user?.id ? (
                <Badge variant="default">You</Badge>
              ) : (
                ""
              )}
            </p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant={user.role === "admin" ? "destructive" : "default"}>
          {user.role.toUpperCase()}
        </Badge>
      </TableCell>
      <TableCell>
        <Badge variant={user.subscriptionStatus ? "default" : "secondary"}>
          {user.subscriptionStatus ? "SUBSCRIBED" : "UNSUBSCRIBED"}
        </Badge>
      </TableCell>
      <TableCell className="text-muted-foreground">
        {moment(user.createdAt).format("MMM DD, YYYY")}
      </TableCell>
      <TableCell>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              Actions
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={handleMakeAdmin}
              disabled={user.role === "admin"}
            >
              <RiVipCrownLine className="mr-2 h-4 w-4" />
              Make Admin
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={handleMakeNormalUser}
              disabled={user.role !== "admin"}
            >
              <RiVipCrownLine className="mr-2 h-4 w-4" />
              Make normal user
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
};
